import { CardEnvelopeDesign } from '../../CardCustomizer.types';
import type { LayerData, TextLayerData } from '../../components/Layer.types';

// A2 envelope width (7.25 inches) in points (72pt = 1 inch).
export const CANVAS_WIDTH_PT = 7.25 * 72; // 522pt

export const DEFAULT_ENVELOPE_THEME_ID = 'traditional_envelope';

export const isEditableTextLayer = (layer: LayerData): layer is TextLayerData => layer.type === 'text' && layer.editable;
// Legacy drafts stored CardEnvelopeDesign enum values ('traditional'|'script'|'modern').
// New drafts store the full themeId ('traditional_envelope' etc.).
// Referenced in CardCustomizer.types.ts Envelope.design field — keep in sync if designs change.
export const LEGACY_DESIGN_TO_THEME_ID: Record<string, string> = {
  [CardEnvelopeDesign.traditional]: 'traditional_envelope',
  [CardEnvelopeDesign.script]: 'script_envelope',
  [CardEnvelopeDesign.modern]: 'modern_envelope',
};

// Back templates are laid out for physical printing (editable text has rotation: -180, positioned
// near the seal flap). Repositions text layers to a UI-friendly centered layout so preview
// canvas renders correctly. card_service's applyEnvelopeFlapOffset also expects y≈40-60 range
// (formula: pdfY = FLAP_HEIGHT - (height + y)); raw y≈330 gives pdfY=-188 (off-canvas in PDF).
export const editableTextLayersSortedByHeight = (layers: LayerData[]): TextLayerData[] => {
  return layers.filter(isEditableTextLayer).sort((a, b) => a.layout.height - b.layout.height);
};

export const formatEnvelopeDesignName = (themeId: string): string => {
  return (
    themeId
      .replace(/_envelope$/, '')
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || themeId
  );
};

// Callers are responsible for passing only editable text layers.
export const normalizeBackTextLayersForDisplay = (layers: TextLayerData[]): TextLayerData[] => {
  // Sort by height ascending: name (1 line, short) < address (multi-line, tall).
  const sorted = [...layers].sort((a, b) => a.layout.height - b.layout.height);

  let currentY = 40;
  return sorted.map(layer => {
    const x = Math.round((CANVAS_WIDTH_PT - layer.layout.width) / 2);
    const y = currentY;
    currentY += layer.layout.height + 4;
    return { ...layer, layout: { ...layer.layout, x, y, rotation: 0 } };
  });
};

/**
 * Return-address layers as they should DISPLAY on an envelope face: images first so artwork never
 * paints over the addressing, and back-flap text normalized out of its printing coordinates.
 *
 * `Envelope.tsx` and `Recipients.tsx` compose the same halves inline, each with an extra concern
 * this does not cover. This is the plain case: one face, no recipient.
 */
export const composeEnvelopeReturnLayersForDisplay = (layers: LayerData[], isBackLayout: boolean): LayerData[] => {
  const images = layers.filter(layer => layer.type === 'image');
  const text = layers.filter(isEditableTextLayer);
  return [...images, ...(isBackLayout ? normalizeBackTextLayersForDisplay(text) : text)];
};
